<template>
  <div class="registration">
    <template v-if="step === 'form'">
      <div class="form container">
        <div class="row margin-bottom-form-field">
          <div class="col-xs-12">
            <input
              v-model="form.firstName"
              :class="`tb ${errors.firstName.length ? 'error' : ''}`"
              type="text"
              placeholder="Ime"
            />
            <p class="registration-error" v-if="errors.firstName.length">
              {{ errors.firstName }}
            </p>
          </div>
        </div>
        <div class="row margin-bottom-form-field">
          <div class="col-xs-12">
            <input
              v-model="form.lastName"
              :class="`tb ${errors.lastName.length ? 'error' : ''}`"
              type="text"
              placeholder="Prezime"
            />
            <p class="registration-error" v-if="errors.lastName.length">
              {{ errors.lastName }}
            </p>
          </div>
        </div>
        <div class="row margin-bottom-form-field">
          <div class="col-xs-12">
            <input
              v-model="form.username"
              :class="`tb ${errors.username.length ? 'error' : ''}`"
              type="text"
              placeholder="Korisničko ime"
            />
            <p class="registration-error" v-if="errors.username.length">
              {{ errors.username }}
            </p>
          </div>
        </div>
        <div class="row margin-bottom-form-field" v-if="registerWithPhone">
          <div class="col-xs-12">
            <div :class="`phone-field ${errors.phone.length ? 'error' : ''}`">
              <div class="country-picker" ref="countryPickerRef" @click.stop>
                <button
                  type="button"
                  class="country-trigger"
                  :aria-expanded="isCountryDropdownOpen"
                  @click="toggleCountryDropdown"
                >
                  <img
                    class="country-flag"
                    :src="selectedCountryFlag"
                    :alt="selectedCountry.name"
                    width="24"
                    height="18"
                  />
                  <span class="country-dial">+{{ selectedCountry.dialCode }}</span>
                  <span class="country-caret" :class="{ open: isCountryDropdownOpen }"></span>
                </button>
                <div v-if="isCountryDropdownOpen" class="country-dropdown">
                  <div class="country-search-wrap">
                    <input
                      ref="countrySearchRef"
                      v-model="countrySearch"
                      class="country-search"
                      type="text"
                      inputmode="search"
                      placeholder="Pretraži državu..."
                      autocomplete="off"
                      @click.stop
                      @keydown.esc.stop="closeCountryDropdown"
                    />
                  </div>
                  <div class="country-options">
                    <button
                      v-for="country in filteredCountries"
                      :key="country.iso2"
                      type="button"
                      class="country-option"
                      :class="{ active: country.iso2 === selectedCountryIso2 }"
                      @click="selectCountry(country.iso2)"
                    >
                      <img
                        class="country-flag"
                        :src="getCountryFlagUrl(country.iso2)"
                        :alt="country.name"
                        width="24"
                        height="18"
                      />
                      <span class="country-option-name">{{ country.name }}</span>
                      <span class="country-option-dial">+{{ country.dialCode }}</span>
                    </button>
                    <p v-if="!filteredCountries.length" class="country-empty">Nema rezultata</p>
                  </div>
                </div>
              </div>
              <input
                v-model="form.phone"
                class="phone-input"
                type="tel"
                inputmode="numeric"
                autocomplete="tel-national"
                placeholder="641234567"
                @input="onPhoneInput"
              />
            </div>
            <p class="registration-error" v-if="errors.phone.length">{{ errors.phone }}</p>
            <button type="button" class="alt-method-link" @click="switchToEmail">
              Koristi email umesto telefona
            </button>
          </div>
        </div>
        <div class="row margin-bottom-form-field" v-else>
          <div class="col-xs-12">
            <input
              v-model="form.email"
              :class="`tb ${errors.email.length ? 'error' : ''}`"
              type="email"
              autocomplete="email"
              placeholder="Email"
            />
            <p class="registration-error" v-if="errors.email.length">{{ errors.email }}</p>
            <button type="button" class="alt-method-link" @click="switchToPhone">
              Koristi telefon umesto emaila
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-6">
            <VueDatePicker
              ref="datePickerInput"
              v-model="form.birthday"
              :clearable="false"
              placeholder="DD-MM-YYYY"
              auto-apply
              :action-row="{
                showNow: false,
                showPreview: false,
                showSelect: false,
                showCancel: false,
              }"
              id="birthday"
              :enable-time-picker="false"
              format="dd-MM-yyyy"
            >
              <template #input-icon>
                <img class="input-slot-image" src="@/assets/images/cake.png" />
              </template>
            </VueDatePicker>
          </div>
          <div class="col-xs-6">
            <input
              v-model="form.promoCode"
              class="tb-half"
              type="text"
              placeholder="Promo kod"
            />
          </div>
          <p
            class="registration-error"
            style="margin-left: 15px"
            v-if="errors.birthday.length"
          >
            {{ errors.birthday }}
          </p>
        </div>
        <div class="separator"></div>
      </div>
      <div class="conditions">
        <div class="conditions-wrapper">
          <input v-model="form.acceptedTerms" type="checkbox" />&nbsp;Prihvatam
          <span class="conditions-text"
            >uslove/pravila korišćenja i pravila<br />
            o privatnosti *</span
          ><br />
          <p class="registration-error" v-if="errors.acceptedTerms.length">
            * Polje je obavezno
          </p>
        </div>
        <div class="conditions-wrapper">
          <input v-model="form.isAdult" type="checkbox" />&nbsp;Da, imam
          <span class="conditions-text">više od 18 godina *</span>
          <p class="registration-error" v-if="errors.isAdult.length">* Polje je obavezno</p>
        </div>
        <div class="conditions-wrapper">
          <input v-model="form.agreedNotifications" type="checkbox" />&nbsp;Slažem se da
          primam promotivne poruke, i ponude putem email-a, SMS-a i drugih vidova
          komunikacije
        </div>
      </div>
      <p class="error-text">{{ getRegistrationErrorMessage }}</p>
      <div class="submit">
        <button class="btnSubmit" :disabled="isSubmittingAccount || isLoadingCreateAccount" @click="handleCreateAccount()">
          {{ isSubmittingAccount || isLoadingCreateAccount ? "Učitavanje..." : "Kreirajte nalog" }}
        </button>
      </div>
    </template>

    <template v-else>
      <div class="otp-step">
        <p class="otp-title">Potvrda broja telefona</p>
        <p class="otp-description">
          Unesite 6-cifreni kod koji smo poslali na<br />
          <span class="otp-phone">{{ fullPhoneNumber }}</span>
        </p>

        <div class="otp-inputs" @paste="onOtpPaste">
          <input
            v-for="(_, index) in otpDigits"
            :key="`${otpInputsKey}-${index}`"
            :ref="(el) => setOtpInputRef(el, index)"
            :value="otpDigits[index]"
            :class="['otp-input', { error: Boolean(otpError) }]"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            :disabled="isLoadingCheckOtp"
            @input="onOtpInput($event, index)"
            @keydown="onOtpKeydown($event, index)"
            @focus="onOtpFocus(index)"
          />
        </div>

        <p class="registration-error otp-error" v-if="otpError">{{ otpError }}</p>
        <p class="error-text">{{ getRegistrationErrorMessage }}</p>
        <p v-if="isLoadingCheckOtp" class="otp-loading">Provera koda...</p>

        <div class="otp-resend">
          <button
            type="button"
            class="alt-method-link"
            :disabled="resendSecondsLeft > 0 || isSubmittingAccount || isLoadingCreateAccount || isLoadingCheckOtp"
            @click="handleResendOtp"
          >
            <template v-if="resendSecondsLeft > 0">
              Pošalji ponovo ({{ resendSecondsLeft }}s)
            </template>
            <template v-else>Pošalji kod ponovo</template>
          </button>
          <button type="button" class="alt-method-link otp-back" @click="backToForm">
            Nazad na registraciju
          </button>
        </div>
      </div>
    </template>
  </div>

  <div v-if="showSuccessModal" class="success-modal-overlay" @click.self="confirmRegistrationRedirect">
    <div class="success-modal">
      <p class="success-modal-message">Nalog je uspešno kreiran. Proverite Vašu email adresu.</p>
      <button type="button" class="success-modal-btn" @click="confirmRegistrationRedirect">Potvrdi</button>
    </div>
  </div>
</template>

<script lang="ts">
import store from "@/store";
import {
  buildInternationalPhone,
  DEFAULT_COUNTRY_ISO2,
  findCountryByIso2,
  getCountries,
  getCountryFlagUrl,
  normalizeNationalNumber,
  type CountryDialOption,
} from "@/data/countries";
import { Options, Vue } from "vue-class-component";
import { ref } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { mapGetters } from "vuex";

type RegistrationStep = "form" | "otp";

@Options({
  _components: {
    VueDatePicker,
  },
  get components() {
    return this._components;
  },
  set components(value) {
    this._components = value;
  },
  computed: {
    ...mapGetters(["getRegistrationErrorMessage", "isLoadingCreateAccount", "isLoadingCheckOtp"]),
  },
})
export default class Registration extends Vue {
  step: RegistrationStep = "form";
  registerWithPhone = true;
  showSuccessModal = false;
  registrationUuid = "";
  otpDigits: string[] = ["", "", "", "", "", ""];
  otpInputsKey = 0;
  otpError = "";
  otpInputRefs: (HTMLInputElement | null)[] = [];
  resendSecondsLeft = 0;
  resendTimerId: ReturnType<typeof setInterval> | null = null;
  countries: CountryDialOption[] = getCountries();
  selectedCountryIso2 = DEFAULT_COUNTRY_ISO2;
  isCountryDropdownOpen = false;
  countrySearch = "";
  isSubmittingAccount = false;
  onDocumentClickBound = (event: MouseEvent) => this.handleDocumentClick(event);

  form = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    birthday: "",
    acceptedTerms: false,
    isAdult: false,
    agreedNotifications: false,
    promoCode: "",
    affiliate: null,
  };
  errors = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    birthday: "",
    acceptedTerms: "",
    isAdult: "",
  };
  badRequestErrorMessages = {
    firstName: "Pogrešan format imena",
    lastName: "Pogrešan format prezimena",
    username: "Format korisničkog imena nije odgovarajući",
    email: "Format email adrese nije odgovarajući",
    phone: "Format broja telefona nije odgovarajući",
    birthday: "Pogrešan format datuma rođenja",
    acceptedTerms:
      "Potrebna je saglasnost sa uslovima/pravilima korišćenja i pravilima o privatnosti",
    isAdult: "Potrebna je saglasnost o punoletstvu",
  };
  conflictingErrorMessages = {
    firstName: "Pogrešno ime",
    lastName: "Pogrešno prezime",
    username: "Korisničko ime već postoji",
    email: "Email već postoji",
    phone: "Broj telefona već postoji",
    birthday: "Pogrešan datum rođenja",
    acceptedTerms: "Pogrešna saglasnost sa uslovima",
    isAdult: "Pogrešna saglasnost o punoletstvu",
  };

  public getRegistrationErrorMessage: any;
  public isLoadingCreateAccount: any;
  public isLoadingCheckOtp: any;

  get selectedCountry(): CountryDialOption {
    return findCountryByIso2(this.selectedCountryIso2) || this.countries[0];
  }

  get selectedCountryFlag(): string {
    return getCountryFlagUrl(this.selectedCountry.iso2);
  }

  get filteredCountries(): CountryDialOption[] {
    const query = this.countrySearch.trim().toLowerCase().replace(/^\+/, "");
    if (!query) return this.countries;

    return this.countries.filter((country) => {
      const name = country.name.toLowerCase();
      const dial = country.dialCode.toLowerCase();
      const iso = country.iso2.toLowerCase();
      return name.includes(query) || dial.includes(query) || iso.includes(query) || `+${dial}`.includes(query);
    });
  }

  get fullPhoneNumber(): string {
    return buildInternationalPhone(this.selectedCountry.dialCode, this.form.phone);
  }

  get isOtpComplete(): boolean {
    return this.otpDigits.every((digit) => digit.length === 1);
  }

  get otpValue(): string {
    return this.otpDigits.join("");
  }

  get isAdultValid() {
    if (!this.form.birthday) {
      return 0;
    }

    const birthDate = new Date(this.form.birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();
    const day = today.getDate() - birthDate.getDate();

    if (month < 0 || (month === 0 && day < 0)) {
      age--;
    }

    return age;
  }

  mounted() {
    const token = this.$route.query.token;
    if (token) {
      store.commit("SET_TOKEN", token);
      localStorage.setItem("token", JSON.stringify(token));
    }

    if (this.step !== "otp") {
      this.setOtpMode(false);
    }
    document.addEventListener("click", this.onDocumentClickBound);
    window.addEventListener("registration-back-requested", this.onRegistrationBackBound);
  }

  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClickBound);
    window.removeEventListener("registration-back-requested", this.onRegistrationBackBound);
    this.clearResendTimer();
    this.setOtpMode(false);
  }

  onRegistrationBackBound = () => {
    if (this.step === "otp") {
      this.backToForm();
    }
  };

  getCountryFlagUrl = getCountryFlagUrl;

  handleDocumentClick(event: MouseEvent) {
    if (!this.isCountryDropdownOpen) return;
    const picker = this.$refs.countryPickerRef as HTMLElement | undefined;
    if (picker && !picker.contains(event.target as Node)) {
      this.closeCountryDropdown();
    }
  }

  toggleCountryDropdown() {
    if (this.isCountryDropdownOpen) {
      this.closeCountryDropdown();
      return;
    }
    this.isCountryDropdownOpen = true;
    this.countrySearch = "";
    this.$nextTick(() => {
      const searchInput = this.$refs.countrySearchRef as HTMLInputElement | undefined;
      searchInput?.focus();
    });
  }

  closeCountryDropdown() {
    this.isCountryDropdownOpen = false;
    this.countrySearch = "";
  }

  selectCountry(iso2: string) {
    this.selectedCountryIso2 = iso2;
    this.closeCountryDropdown();
    this.errors.phone = "";
  }

  onPhoneInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const digitsOnly = target.value.replace(/\D/g, "");
    this.form.phone = digitsOnly;
    target.value = digitsOnly;
    this.errors.phone = "";
  }

  switchToEmail() {
    this.registerWithPhone = false;
    this.errors.phone = "";
    this.form.phone = "";
    this.closeCountryDropdown();
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");
  }

  switchToPhone() {
    this.registerWithPhone = true;
    this.errors.email = "";
    this.form.email = "";
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");
  }

  setOtpMode(active: boolean) {
    store.commit("SET_HIDE_AUTH_TOGGLE_BUTTONS", active);
    window.dispatchEvent(new Event("incode-validation-mode-changed"));
  }

  setOtpInputRef(el: unknown, index: number) {
    this.otpInputRefs[index] = (el as HTMLInputElement) || null;
  }

  focusOtpInput(index: number) {
    const input = this.otpInputRefs[index];
    if (input) {
      input.focus();
      input.select();
    }
  }

  clearOtpDigits(keepError = false) {
    this.otpDigits = ["", "", "", "", "", ""];
    this.otpInputRefs = [];
    this.otpInputsKey += 1;
    if (!keepError) {
      this.otpError = "";
    }
    this.$nextTick(() => this.focusOtpInput(0));
  }

  resetOtpInputs() {
    this.clearOtpDigits(false);
  }

  startResendCooldown(seconds = 60) {
    this.clearResendTimer();
    this.resendSecondsLeft = seconds;
    this.resendTimerId = setInterval(() => {
      if (this.resendSecondsLeft <= 1) {
        this.clearResendTimer();
        this.resendSecondsLeft = 0;
        return;
      }
      this.resendSecondsLeft -= 1;
    }, 1000);
  }

  clearResendTimer() {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  }

  onOtpFocus(index: number) {
    const firstEmpty = this.otpDigits.findIndex((digit) => !digit);
    if (firstEmpty !== -1 && firstEmpty < index) {
      this.focusOtpInput(firstEmpty);
    }
  }

  onOtpInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, "");

    if (!value) {
      this.otpDigits[index] = "";
      this.otpError = "";
      return;
    }

    const chars = value.split("");
    this.otpDigits[index] = chars[0];
    this.otpError = "";

    let nextIndex = index;
    for (let i = 1; i < chars.length && index + i < 6; i++) {
      this.otpDigits[index + i] = chars[i];
      nextIndex = index + i;
    }

    this.otpDigits = [...this.otpDigits];
    target.value = this.otpDigits[index];

    if (nextIndex < 5) {
      this.focusOtpInput(nextIndex + 1);
    } else if (this.isOtpComplete) {
      this.handleVerifyOtp();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    const key = event.key;

    if (key === "Backspace") {
      if (this.otpDigits[index]) {
        this.otpDigits[index] = "";
        this.otpDigits = [...this.otpDigits];
        this.otpError = "";
        return;
      }
      if (index > 0) {
        event.preventDefault();
        this.otpDigits[index - 1] = "";
        this.otpDigits = [...this.otpDigits];
        this.otpError = "";
        this.focusOtpInput(index - 1);
      }
      return;
    }

    if (key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusOtpInput(index - 1);
      return;
    }

    if (key === "ArrowRight" && index < 5) {
      event.preventDefault();
      this.focusOtpInput(index + 1);
      return;
    }

    if (key === "Enter" && this.isOtpComplete) {
      event.preventDefault();
      this.handleVerifyOtp();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const nextDigits = ["", "", "", "", "", ""];
    pasted.split("").forEach((digit, index) => {
      nextDigits[index] = digit;
    });
    this.otpDigits = nextDigits;
    this.otpError = "";

    const focusIndex = Math.min(pasted.length, 5);
    this.$nextTick(() => {
      this.focusOtpInput(focusIndex);
      if (this.isOtpComplete) {
        this.handleVerifyOtp();
      }
    });
  }

  extractErrors(data: unknown): string[] {
    if (typeof data === "string") {
      return [data];
    }
    if (Array.isArray(data)) {
      return data.map(String);
    }
    if (!data || typeof data !== "object") {
      return [];
    }

    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.errors)) {
      return obj.errors.map(String);
    }
    if (obj.errors && typeof obj.errors === "object") {
      return Object.values(obj.errors as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map(String);
    }
    if (typeof obj.title === "string") {
      return [obj.title];
    }
    if (typeof obj.message === "string") {
      return [obj.message];
    }
    return [];
  }

  validateForm() {
    this.errors = {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      phone: "",
      birthday: "",
      acceptedTerms: "",
      isAdult: "",
    };
    if (!this.form.firstName) this.errors.firstName = "Ime je obavezno";
    if (!this.form.lastName) this.errors.lastName = "Prezime je obavezno";
    if (!this.form.username) this.errors.username = "Korisničko ime je obavezno";
    if (!this.form.isAdult) this.errors.isAdult = "Potrebna je saglasnost o punoletstvu";
    if (!this.form.acceptedTerms)
      this.errors.acceptedTerms =
        "Potrebna je saglasnost sa uslovima/pravilima korišćenja i pravilima o privatnosti";

    if (this.registerWithPhone) {
      const national = normalizeNationalNumber(this.form.phone);
      if (!this.form.phone.trim()) {
        this.errors.phone = "Broj telefona je obavezan";
      } else if (!/^[0-9]{6,12}$/.test(national)) {
        this.errors.phone = "Format broja telefona nije odgovarajući";
      }
    } else {
      if (!this.form.email) {
        this.errors.email = "Email je obavezan";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.email)) {
          this.errors.email = "Format email adrese nije odgovarajući";
        }
      }
    }

    const usernameRegex = /^[a-zA-Z][a-zA-Z0-9]{4,15}$/;
    if (!usernameRegex.test(this.form.username) && this.form.username) {
      this.errors.username = "Format korisničkog imena nije odgovarajući";
    }

    if (!this.form.birthday) this.errors.birthday = "Datum rođenja je obavezan";

    if (this.isAdultValid < 18 && this.form.isAdult) {
      if (this.form.birthday) {
        this.errors.birthday = "Datum rođenja ne odgovara saglasnosti o punoletstvu";
      }
    }

    const hasErrors = Object.values(this.errors).some((value) => value !== "");
    return !hasErrors;
  }

  clearFieldErrors() {
    this.errors.firstName = "";
    this.errors.lastName = "";
    this.errors.username = "";
    this.errors.email = "";
    this.errors.phone = "";
    this.errors.birthday = "";
    this.errors.acceptedTerms = "";
    this.errors.isAdult = "";
  }

  applyApiFieldErrors(status: number, errorKeys: string[]) {
    for (const key of errorKeys) {
      if (!(key in this.errors)) continue;
      const typedKey = key as keyof typeof this.errors;

      if (status === 400) {
        this.errors[typedKey] = this.badRequestErrorMessages[typedKey];
      } else if (status === 409) {
        this.errors[typedKey] = this.conflictingErrorMessages[typedKey];
      }
    }
  }

  handleRegistrationApiErrors(error: any): boolean {
    if (!error || !("status" in error) || !("data" in error)) return false;

    const status = error.status as number;
    const errorKeys = this.extractErrors(error.data);

    if (errorKeys.includes("OTPMaxAttemptsExceeded")) {
      store.commit(
        "SET_REGISTRATION_ERROR_MESSAGE",
        "Prekoračen je broj pokušaja slanja koda. Pokušajte kasnije."
      );
      return true;
    }

    this.applyApiFieldErrors(status, errorKeys);
    return true;
  }

  enterOtpStep(uuid: string) {
    this.registrationUuid = uuid;
    this.step = "otp";
    this.setOtpMode(true);
    this.resetOtpInputs();
    this.startResendCooldown();
  }

  backToForm() {
    this.step = "form";
    this.registrationUuid = "";
    this.resetOtpInputs();
    this.clearResendTimer();
    this.resendSecondsLeft = 0;
    this.setOtpMode(false);
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");
  }

  resetFormFields() {
    this.form.firstName = "";
    this.form.lastName = "";
    this.form.username = "";
    this.form.email = "";
    this.form.phone = "";
    this.form.birthday = "";
    this.form.acceptedTerms = false;
    this.form.isAdult = false;
    this.form.agreedNotifications = false;
    this.form.promoCode = "";
    this.form.affiliate = null;
    this.selectedCountryIso2 = DEFAULT_COUNTRY_ISO2;
  }

  getRegistrationPayload() {
    return {
      ...this.form,
      phone: this.fullPhoneNumber,
      registerWithPhone: this.registerWithPhone,
    };
  }

  async handleCreateAccount() {
    if (this.isSubmittingAccount) return;
    if (!this.validateForm()) {
      return;
    }

    this.isSubmittingAccount = true;
    this.clearFieldErrors();
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");

    try {
      const data = await store.dispatch("createAccount", this.getRegistrationPayload());

      if (this.registerWithPhone) {
        const uuid = data?.uuid;
        if (!uuid) {
          store.commit(
            "SET_REGISTRATION_ERROR_MESSAGE",
            "Nije primljen identifikator za potvrdu. Pokušajte ponovo."
          );
          return;
        }
        this.enterOtpStep(String(uuid));
        return;
      }

      this.resetFormFields();
      this.showSuccessModal = true;
    } catch (error: any) {
      this.handleRegistrationApiErrors(error);
    } finally {
      this.isSubmittingAccount = false;
    }
  }

  async handleResendOtp() {
    if (this.resendSecondsLeft > 0 || this.isSubmittingAccount || this.isLoadingCreateAccount || this.isLoadingCheckOtp) return;

    this.isSubmittingAccount = true;
    this.otpError = "";
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");

    try {
      const data = await store.dispatch("createAccount", this.getRegistrationPayload());
      const uuid = data?.uuid;
      if (uuid) {
        this.registrationUuid = String(uuid);
      }
      this.resetOtpInputs();
      this.startResendCooldown();
    } catch (error: any) {
      this.handleRegistrationApiErrors(error);
    } finally {
      this.isSubmittingAccount = false;
    }
  }

  async handleVerifyOtp() {
    if (!this.isOtpComplete || this.isLoadingCheckOtp) return;

    this.otpError = "";
    store.commit("SET_REGISTRATION_ERROR_MESSAGE", "");

    try {
      const data = await store.dispatch("checkOtp", {
        uuid: this.registrationUuid,
        otp: this.otpValue,
      });

      const redirectUrl = data?.url;
      if (!redirectUrl || typeof redirectUrl !== "string") {
        store.commit(
          "SET_REGISTRATION_ERROR_MESSAGE",
          "Nije primljen URL za nastavak. Pokušajte ponovo."
        );
        return;
      }

      this.clearResendTimer();
      this.setOtpMode(false);
      window.location.href = redirectUrl;
    } catch (error: any) {
      if (!error || !("status" in error) || !("data" in error)) {
        this.otpError = "Neispravan kod. Pokušajte ponovo.";
        this.clearOtpDigits(true);
        return;
      }

      const errorKeys = this.extractErrors(error.data);
      const joined = errorKeys.join(" ").toLowerCase();

      if (
        errorKeys.includes("OTPWrong") ||
        joined.includes("otpwrong") ||
        (error.status === 400 &&
          !errorKeys.includes("OTPNotFound") &&
          !errorKeys.includes("OTPExpired") &&
          !joined.includes("otpnotfound") &&
          !joined.includes("otpexpired"))
      ) {
        this.otpError = "Neispravan kod. Pokušajte ponovo.";
        this.clearOtpDigits(true);
        return;
      }

      if (
        errorKeys.includes("OTPNotFound") ||
        errorKeys.includes("OTPExpired") ||
        joined.includes("otpnotfound") ||
        joined.includes("otpexpired")
      ) {
        this.otpError = "Kod je istekao. Pokušajte kasnije ili pošaljite novi kod.";
        this.clearOtpDigits(true);
        return;
      }

      this.otpError = "Došlo je do greške prilikom potvrde koda. Pokušajte ponovo.";
      this.clearOtpDigits(true);
    }
  }

  confirmRegistrationRedirect() {
    this.showSuccessModal = false;
    this.$router.push("/login");
  }
}
</script>
<style scoped>
@media (max-width: 480px) {
  .dp--tp-wrap {
    display: none !important;
  }
  .input-slot-image {
    height: 25px;
    width: auto;
    margin-left: 110px;
  }

  .error-text {
    color: red !important;
    font-size: 14px !important;
    margin-top: 0px !important;
    font-weight: normal !important;
    text-transform: none !important;
    text-align: center !important;
  }

  :deep()[data-test="dp-input"] {
    margin: 0px !important;
    color: white !important;
    height: 60px !important;
    gap: 0px !important;
    border-radius: 5px !important;
    background: rgba(35, 35, 35, 1) !important;
    border: none !important;
    font-family: "Saira";
    font-size: 15px;
    text-align: left !important;
    padding-right: 0px !important;
    padding-left: 10px !important;
    width: 148px;
  }

  .separator {
    margin-bottom: 30px;
  }
  input {
    color: rgba(156, 156, 156, 1) !important;
    padding-left: 18px;
    font-size: 18px;
  }
  .form {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .margin-bottom-form-field {
    margin-bottom: 10px !important;
  }

  .tb {
    height: 60px;
    width: 326px;
    gap: 0px;
    border-radius: 5px;
    margin: 0 auto;
    background: rgba(35, 35, 35, 1);
  }

  .tb-half {
    height: 60px;
    border-radius: 5px;
    margin: 0 auto;
    width: 148px;
    background: rgba(35, 35, 35, 1);
  }

  .registration p {
    text-transform: uppercase;
    text-align: center;
    margin-top: 38px;
    font-size: 38px;
    font-weight: 700;
    text-align: center;
    color: white;
  }

  .conditions {
    color: white;
    width: 330px;
    margin: 0 auto;
  }

  .conditions-text {
    color: rgba(24, 255, 75, 1);
  }
  .conditions-wrapper {
    margin-bottom: 10px;
  }
  .submit {
    display: flex;
    justify-content: center;
    margin-top: 30px;
  }
  .btnSubmit {
    width: 70% !important;
    height: 67px !important;
    padding: 16px 69px 16px 69px !important;
    gap: 8px !important;
    border-radius: 8px !important;
    color: white !important;
    background: rgba(24, 171, 63, 1) !important;
    box-shadow: 0px 9px 16px 0px rgba(214, 255, 200, 0.45) inset !important;
    box-shadow: 0px -6px 10px 0px rgba(0, 98, 3, 0.52) inset !important;
    margin-bottom: 20px;
  }

  .btnSubmit:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
  }

  .registration-error {
    color: red !important;
    font-size: 14px !important;
    margin-top: 0px !important;
    font-weight: normal !important;
    text-transform: none !important;
    text-align: left !important;
  }

  .error {
    border: 1px solid red;
  }

  .cake-date {
    position: absolute;
    top: 14px;
    right: 31px;
    z-index: 0 !important;
  }

  .phone-field {
    width: 326px;
    margin: 0 auto;
    height: 60px;
    border-radius: 5px;
    background: rgba(35, 35, 35, 1);
    display: flex;
    align-items: center;
    position: relative;
  }

  .phone-field.error {
    border: 1px solid red;
  }

  .country-picker {
    position: relative;
    flex-shrink: 0;
    height: 100%;
  }

  .country-trigger {
    height: 100%;
    min-width: 102px;
    padding: 0 10px 0 12px;
    border: none;
    border-right: 1px solid rgba(70, 70, 70, 1);
    background: transparent;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: rgba(200, 200, 200, 1);
    font-family: "Saira", sans-serif;
    font-size: 15px;
  }

  .country-flag {
    width: 24px;
    height: 18px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .country-dial {
    color: white;
    font-weight: 600;
  }

  .country-caret {
    width: 0;
    height: 0;
    margin-left: 2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid rgba(156, 156, 156, 1);
    transition: transform 0.15s ease;
  }

  .country-caret.open {
    transform: rotate(180deg);
  }

  .country-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 326px;
    background: rgba(35, 35, 35, 1);
    border: 1px solid rgba(70, 70, 70, 1);
    border-radius: 8px;
    z-index: 20;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }

  .country-search-wrap {
    padding: 10px;
    border-bottom: 1px solid rgba(70, 70, 70, 1);
  }

  .country-search {
    width: 100%;
    height: 40px;
    border-radius: 6px !important;
    border: none !important;
    background: rgba(24, 24, 24, 1) !important;
    color: white !important;
    padding: 0 12px !important;
    font-size: 14px !important;
    font-family: "Saira", sans-serif;
  }

  .country-search::placeholder {
    color: rgba(120, 120, 120, 1);
  }

  .country-options {
    max-height: 200px;
    overflow-y: auto;
  }

  .country-empty {
    margin: 16px 0 !important;
    padding: 0 14px;
    color: rgba(156, 156, 156, 1) !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    text-transform: none !important;
    text-align: center !important;
  }

  .country-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: none;
    background: transparent;
    color: rgba(200, 200, 200, 1);
    font-family: "Saira", sans-serif;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }

  .country-option:hover,
  .country-option.active {
    background: rgba(24, 171, 63, 0.18);
    color: white;
  }

  .country-option-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-option-dial {
    color: rgba(156, 156, 156, 1);
    flex-shrink: 0;
  }

  .phone-input {
    flex: 1;
    height: 100%;
    min-width: 0;
    border: none !important;
    background: transparent !important;
    color: rgba(156, 156, 156, 1) !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
    font-size: 18px !important;
    font-family: "Saira", sans-serif;
  }

  .alt-method-link {
    display: block;
    width: 326px;
    margin: 8px auto 0;
    padding: 0;
    border: none;
    background: transparent;
    color: rgba(156, 156, 156, 1);
    font-family: "Saira", sans-serif;
    font-size: 13px;
    font-weight: 400;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  .alt-method-link:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    text-decoration: none;
  }

  .otp-step {
    width: 326px;
    margin: 0 auto;
    text-align: center;
  }

  .otp-title {
    text-transform: none !important;
    font-size: 22px !important;
    font-weight: 700 !important;
    margin: 12px 0 10px !important;
    color: white !important;
  }

  .otp-description {
    text-transform: none !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin: 0 0 24px !important;
    line-height: 1.45 !important;
    color: rgba(156, 156, 156, 1) !important;
  }

  .otp-phone {
    color: rgba(24, 255, 75, 1);
    font-weight: 600;
  }

  .otp-inputs {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
  }

  .otp-input {
    width: 46px !important;
    height: 56px !important;
    padding: 0 !important;
    border-radius: 8px !important;
    background: rgba(35, 35, 35, 1) !important;
    color: white !important;
    font-family: "Saira", sans-serif !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    text-align: center !important;
    border: 1px solid transparent;
    caret-color: rgba(24, 171, 63, 1);
  }

  .otp-input:focus {
    border-color: rgba(24, 171, 63, 1);
    outline: none;
  }

  .otp-input.error {
    border: 1px solid red !important;
  }

  .otp-error {
    text-align: center !important;
    margin-bottom: 8px !important;
  }

  .otp-loading {
    text-transform: none !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    margin: 8px 0 4px !important;
    color: rgba(156, 156, 156, 1) !important;
  }

  .otp-resend {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
    margin-bottom: 24px;
  }

  .otp-resend .alt-method-link {
    width: auto;
    text-align: center;
    margin: 0;
  }

  .otp-back {
    color: rgba(120, 120, 120, 1) !important;
  }
}
</style>
<style>
.success-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-modal {
  background: rgba(35, 35, 35, 1);
  border-radius: 12px;
  padding: 28px 32px;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.success-modal-message {
  color: white;
  font-size: 18px;
  margin: 0 0 24px 0;
  line-height: 1.4;
}

.success-modal-btn {
  padding: 12px 32px;
  border-radius: 8px;
  border: none;
  background: rgba(24, 171, 63, 1);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.success-modal-btn:hover {
  opacity: 0.9;
}
</style>
